import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '../../styles/colors';
import { FaMapMarkerAlt, FaRoad, FaTrain, FaGraduationCap, FaHospital, FaShoppingBag } from 'react-icons/fa';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LocationSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(to bottom, #fff 0%, #f9f9f9 100%);
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: ${colors.primary};
  text-align: center;
  margin-bottom: 2rem;
  animation: ${fadeIn} 1s ease-out;
`;

const LocationContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AddressDetails = styled.div`
  animation: ${fadeIn} 1s ease-out 0.2s;
  opacity: 0;
  animation-fill-mode: forwards;
`;

const AddressTitle = styled.h3`
  font-size: 1.8rem;
  color: ${colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AddressText = styled.p`
  font-size: 1.1rem;
  color: ${colors.text};
  line-height: 1.6;
  margin-bottom: 30px;
`;

const AmenitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AmenityItem = styled.div`
  background: ${colors.white};
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${fadeIn} 1s ease-out ${props => props.delay}s;
  opacity: 0;
  animation-fill-mode: forwards;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const AmenityTitle = styled.h4`
  color: ${colors.primary};
  font-size: 1.2rem;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: ${colors.accent};
  }
`;

const AmenityList = styled.ul`
  list-style: none;
  padding: 0;
`;

const AmenityListItem = styled.li`
  color: ${colors.text};
  margin-bottom: 8px;
  font-size: 1rem;
  position: relative;
  padding-left: 20px;

  &:before {
    content: "•";
    color: ${colors.accent};
    position: absolute;
    left: 0;
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 450px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 1s ease-out 0.4s;
  opacity: 0;
  animation-fill-mode: forwards;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const Location = () => {
  return (
    <LocationSection id="location">
      <Container>
        <Title>Prime Connectivity in Mulund West</Title>
        
        <LocationContent>
          <AddressDetails>
            <AddressTitle>
              <FaMapMarkerAlt /> Address
            </AddressTitle>
            <AddressText>
              Codename Forresta, Agarwal Road, Veena Nagar, Mulund (West), Mumbai, Maharashtra 400080.
            </AddressText>
            
            <AmenitiesGrid>
              <AmenityItem delay={0.6}>
                <AmenityTitle>
                  <FaRoad /> Major Roads
                </AmenityTitle>
                <AmenityList>
                  <AmenityListItem>Eastern Express Highway</AmenityListItem>
                  <AmenityListItem>LBS Marg</AmenityListItem>
                  <AmenityListItem>Mulund-Goregaon Link Road</AmenityListItem>
                </AmenityList>
              </AmenityItem>
              
              <AmenityItem delay={0.8}>
                <AmenityTitle>
                  <FaTrain /> Transportation
                </AmenityTitle>
                <AmenityList>
                  <AmenityListItem>Mulund Railway Station - 5 mins</AmenityListItem>
                  <AmenityListItem>Metro Station (Upcoming)</AmenityListItem>
                </AmenityList>
              </AmenityItem>
              
              <AmenityItem delay={1.0}>
                <AmenityTitle>
                  <FaGraduationCap /> Education
                </AmenityTitle>
                <AmenityList>
                  <AmenityListItem>DAV International School</AmenityListItem>
                  <AmenityListItem>Mulund College of Commerce</AmenityListItem>
                  <AmenityListItem>NES International School</AmenityListItem>
                </AmenityList>
              </AmenityItem>
              
              <AmenityItem delay={1.2}>
                <AmenityTitle>
                  <FaHospital /> Healthcare
                </AmenityTitle>
                <AmenityList>
                  <AmenityListItem>Fortis Hospital</AmenityListItem>
                  <AmenityListItem>Hira Mongi Navneet Hospital</AmenityListItem>
                  <AmenityListItem>Ashwini Hospital</AmenityListItem>
                </AmenityList>
              </AmenityItem>
              
              <AmenityItem delay={1.4}>
                <AmenityTitle>
                  <FaShoppingBag /> Shopping & Entertainment
                </AmenityTitle>
                <AmenityList>
                  <AmenityListItem>R Mall</AmenityListItem>
                  <AmenityListItem>D Mart</AmenityListItem>
                  <AmenityListItem>Neptune Magnet Mall</AmenityListItem>
                </AmenityList>
              </AmenityItem>
            </AmenitiesGrid>
          </AddressDetails>
          
          <MapContainer>
            <iframe
              title="Codename Forresta Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.4825012874164!2d72.94072427590828!3d19.184915781897275!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b9906f6a01d3%3A0xedeecf77f2e15cc2!2sCodename%20Forresta!5e0!3m2!1sen!2sin!4v1710400544610!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapContainer>
        </LocationContent>
      </Container>
    </LocationSection>
  );
};

export default Location; 